import { ACTIVATION_COLORS } from '../classification';
import type { ActivationStatus, ViewMode } from '../../types/exploration';
import { volumeBubbleStyleTokens as v } from '../bubble/volumeBubbleStyles';

const activationStyles: Record<ActivationStatus, { fill: string; stroke: string }> = {
  active: v.status.keys.active,
  toBeActivated: v.status.keys.toBeActivated,
  potentiallySupported: v.status.keys.potentiallySupported,
  unsupported: v.status.keys.unsupported,
};

type SignalStatusStyleKey = 'supported' | 'active';

const statusStyles = {
  supported: v.status.keys.supported,
  active: v.status.keys.active,
} satisfies Record<SignalStatusStyleKey, { fill: string; stroke: string }>;

export const signalColorTokens = {
  statusKeys: new Set(['supported', 'active']),
  mutedStrokeKeys: new Set(['active']),
  unattachedFill: v.volume.unattached.fill,
  statusStyles,
  mutedStatusStyle: v.status.muted,
  borders: {
    hierarchy: '#DCE5F3',
    hierarchyHigh: v.volume.hover.stroke,
    statusHover: v.status.hover.mutedBorder,
  },
  groupBarUnsupported: '#ffffff',
} as const;

function isSignalStatusStyleKey(key: string): key is SignalStatusStyleKey {
  return key in signalColorTokens.statusStyles;
}

export function isSignalStatus(dataKey: string | undefined | null): boolean {
  return !!dataKey && signalColorTokens.statusKeys.has(dataKey);
}

export function isActivationStatus(
  dataKey: string | undefined | null
): dataKey is ActivationStatus {
  return !!dataKey && dataKey in ACTIVATION_COLORS;
}

export function statusStyleFor(dataKey: string | undefined | null): {
  fill: string;
  stroke: string;
} {
  if (isActivationStatus(dataKey)) {
    return activationStyles[dataKey];
  }
  if (dataKey && isSignalStatusStyleKey(dataKey)) {
    return signalColorTokens.statusStyles[dataKey];
  }
  return signalColorTokens.mutedStatusStyle;
}

export function viewSignalBarColor(view: ViewMode): string {
  return view === 'activation'
    ? 'var(--activation-active)'
    : 'var(--exploration-supported)';
}

export function groupBarSegmentColor(key: string, defaultColor: string): string {
  return key === 'unsupported' ? signalColorTokens.groupBarUnsupported : defaultColor;
}

export function breakdownBarColor(
  key: string,
  options: {
    signalKey: string;
    colorForKey: (key: string) => string;
    colorAllKeys: boolean;
  }
): string {
  if (options.colorAllKeys || key === options.signalKey) {
    return options.colorForKey(key) || 'var(--color-gray-300)';
  }
  return 'var(--color-gray-300)';
}
